import "./style.css";

const STATE_KEY = "__overflowTooltipDirective__";
let tooltipSeed = 0;

function getOptions(binding) {
  const value = binding.value;

  if (value && typeof value === "object") {
    return {
      content: value.content,
      disabled: Boolean(value.disabled),
      placement: value.placement === "bottom" ? "bottom" : "top",
      target: value.target,
    };
  }

  return {
    content: value,
    disabled: false,
    placement: "top",
    target: null,
  };
}

function hasDirectText(el) {
  return Array.prototype.some.call(el.childNodes, (node) => {
    return node.nodeType === 3 && node.textContent.trim();
  });
}

function getTarget(el, binding) {
  const options = getOptions(binding);

  if (options.target && typeof options.target === "string") {
    return el.querySelector(options.target) || el;
  }

  if (!hasDirectText(el) && el.children.length === 1) {
    return el.children[0];
  }

  return el;
}

function setTarget(el, binding) {
  const state = el[STATE_KEY];
  const target = getTarget(el, binding);

  if (state.target === target) return target;

  if (state.target) {
    state.target.classList.remove("overflow-tooltip-content");
  }

  state.target = target;
  state.target.classList.add("overflow-tooltip-content");
  return target;
}

function getContent(target, binding) {
  const options = getOptions(binding);
  const content = options.content;

  if (content !== undefined && content !== null) {
    return String(content).trim();
  }

  return (target.textContent || "").trim();
}

function isOverflowing(target) {
  return target.scrollWidth > target.clientWidth + 1;
}

function getPlacement(preferredPlacement, topSpace, bottomSpace, height) {
  const fallbackPlacement = preferredPlacement === "top" ? "bottom" : "top";
  const space = {
    top: topSpace,
    bottom: bottomSpace,
  };

  if (space[preferredPlacement] >= height) return preferredPlacement;
  if (space[fallbackPlacement] >= height) return fallbackPlacement;

  return topSpace >= bottomSpace ? "top" : "bottom";
}

function positionTooltip(target, tooltip, tooltipContent, options) {
  const triggerRect = target.getBoundingClientRect();
  const viewportPadding = 8;
  const gap = 10;

  tooltip.style.maxWidth = `${Math.max(
    0,
    Math.min(360, window.innerWidth - viewportPadding * 2)
  )}px`;
  tooltipContent.style.maxHeight = "";

  let tooltipRect = tooltip.getBoundingClientRect();
  const topSpace = Math.max(0, triggerRect.top - viewportPadding - gap);
  const bottomSpace = Math.max(
    0,
    window.innerHeight - triggerRect.bottom - viewportPadding - gap
  );
  const placement = getPlacement(
    options.placement,
    topSpace,
    bottomSpace,
    tooltipRect.height
  );
  const availableHeight = placement === "top" ? topSpace : bottomSpace;

  tooltip.setAttribute("data-placement", placement);
  tooltipContent.style.maxHeight = `${availableHeight}px`;
  tooltipRect = tooltip.getBoundingClientRect();

  const maxLeft = window.innerWidth - tooltipRect.width - viewportPadding;
  const centeredLeft =
    triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;

  tooltip.style.left = `${Math.max(
    viewportPadding,
    Math.min(centeredLeft, maxLeft)
  )}px`;
  tooltip.style.top = `${
    placement === "top"
      ? triggerRect.top - tooltipRect.height - gap
      : triggerRect.bottom + gap
  }px`;
}

function hideTooltip(el) {
  const state = el[STATE_KEY];

  if (!state || !state.tooltip) return;

  window.removeEventListener("resize", state.reposition);
  window.removeEventListener("scroll", state.reposition, true);
  if (state.tooltip.parentNode) {
    state.tooltip.parentNode.removeChild(state.tooltip);
  }
  state.tooltip = null;
  state.tooltipContent = null;
  state.reposition = null;

  if (state.previousDescribedBy) {
    el.setAttribute("aria-describedby", state.previousDescribedBy);
  } else {
    el.removeAttribute("aria-describedby");
  }
}

function showTooltip(el) {
  const state = el[STATE_KEY];

  if (!state || state.tooltip) return;

  const options = getOptions(state.binding);
  const target = setTarget(el, state.binding);
  const content = getContent(target, state.binding);

  if (options.disabled || !content || !isOverflowing(target)) return;

  const tooltip = document.createElement("div");
  const tooltipContent = document.createElement("div");
  const tooltipId = `overflow-tooltip-${++tooltipSeed}`;

  tooltip.id = tooltipId;
  tooltip.className = "overflow-tooltip";
  tooltip.setAttribute("role", "tooltip");
  tooltipContent.className = "overflow-tooltip__content";
  tooltipContent.textContent = content;
  tooltip.appendChild(tooltipContent);
  document.body.appendChild(tooltip);

  state.tooltip = tooltip;
  state.tooltipContent = tooltipContent;
  state.previousDescribedBy = el.getAttribute("aria-describedby");
  state.reposition = () =>
    positionTooltip(
      state.target,
      state.tooltip,
      state.tooltipContent,
      getOptions(state.binding)
    );
  el.setAttribute("aria-describedby", tooltipId);

  state.reposition();
  window.addEventListener("resize", state.reposition);
  window.addEventListener("scroll", state.reposition, true);
}

const overflowTooltip = {
  bind(el, binding) {
    const state = {
      binding,
      target: null,
      tooltip: null,
      tooltipContent: null,
      previousDescribedBy: null,
      reposition: null,
      show: () => showTooltip(el),
      hide: () => hideTooltip(el),
    };

    el[STATE_KEY] = state;
    el.classList.add("overflow-tooltip-trigger");
    setTarget(el, binding);
    el.addEventListener("mouseenter", state.show);
    el.addEventListener("mouseleave", state.hide);
    el.addEventListener("focus", state.show);
    el.addEventListener("blur", state.hide);
  },

  inserted(el, binding) {
    setTarget(el, binding);
  },

  update(el, binding) {
    const state = el[STATE_KEY];

    if (!state) return;

    state.binding = binding;
    const target = setTarget(el, binding);

    if (!state.tooltip) return;

    const options = getOptions(binding);
    const content = getContent(target, binding);

    if (options.disabled || !content || !isOverflowing(target)) {
      hideTooltip(el);
      return;
    }

    state.tooltipContent.textContent = content;
    state.reposition();
  },

  unbind(el) {
    const state = el[STATE_KEY];

    if (!state) return;

    hideTooltip(el);
    el.removeEventListener("mouseenter", state.show);
    el.removeEventListener("mouseleave", state.hide);
    el.removeEventListener("focus", state.show);
    el.removeEventListener("blur", state.hide);
    el.classList.remove("overflow-tooltip-trigger");
    state.target.classList.remove("overflow-tooltip-content");
    delete el[STATE_KEY];
  },
};

export default overflowTooltip;
